// client/src/pages/seo/DigitalDrinksMenuBarEn.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "A drinks menu should stay fast, clear and current",
  },
  {
    id: "why",
    label: "Why does a digital drinks menu make sense for bars?",
  },
  {
    id: "use-cases",
    label: "Which drinks work especially well in a digital menu?",
  },
  {
    id: "structure",
    label: "How should a digital drinks menu be structured?",
  },
  {
    id: "updates",
    label: "What can a bar update faster online?",
  },
  {
    id: "mistakes",
    label: "What mistakes happen with digital drinks menus?",
  },
  {
    id: "recommendation",
    label: "How would I start as a bar owner?",
  },
  {
    id: "next-steps",
    label: "What should you do now?",
  },
  {
    id: "bestplace",
    label: "How Best Place helps you create a digital drinks menu",
  },
  {
    id: "faq",
    label: "FAQ about digital drinks menus",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalDrinksMenuBarEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Digital Drinks Menu for Bars: Cocktails, Wine and Drinks Online | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/digital-drinks-menu-bar",
          de: "/de/digitale-getraenkekarte-bar",
          no: "/no/digital-drikkemeny-bar",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digital drinks menu for bars: manage cocktails, wine and drinks
              online
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                A drinks menu often changes more than a regular food menu: new
                cocktails, seasonal drinks, unavailable wines, non-alcoholic
                options, updated prices or limited-time offers. This is where a
                digital drinks menu becomes practical.
              </p>

              <p className="seoHero__text">
                Instead of reprinting the menu or replacing a PDF every time,
                the bar can update drinks online. The QR code stays the same,
                while drinks, prices, descriptions and availability can be
                changed.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>A drinks menu should stay fast, clear and current</h2>

          <p>
            Drinks often change at short notice. A wine becomes unavailable, a
            cocktail is replaced for the season, a new non-alcoholic drink is
            added or the bar wants to highlight selected recommendations.
          </p>

          <p>
            A digital drinks menu helps keep this information current without
            reprinting or replacing files again and again. The bar can update
            drinks online while guests continue to use the same QR code.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Weaker</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Printed drinks menu becomes outdated quickly.
                </li>
                <li className="text__seo-li">
                  Unavailable drinks remain visible.
                </li>
                <li className="text__seo-li">
                  New drinks appear too late in the menu.
                </li>
                <li className="text__seo-li">
                  Several PDF versions circulate.
                </li>
                <li className="text__seo-li">
                  Staff constantly explains what is available.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Better</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Update drinks online.</li>
                <li className="text__seo-li">
                  Show available drinks clearly.
                </li>
                <li className="text__seo-li">
                  Add seasonal offers quickly.
                </li>
                <li className="text__seo-li">
                  Use one central digital menu.
                </li>
                <li className="text__seo-li">Keep the same QR code.</li>
              </ul>
            </div>
          </div>

          <h3>Start with a small digital drinks menu</h3>

          <p>
            Add cocktails, wines or non-alcoholic recommendations first and
            check the QR code view on a smartphone. A focused first version is
            easier to test than a long drinks list with every possible category.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why does a digital drinks menu make sense for bars?</h2>

            <p>
              A classic drinks menu works well when the offer is stable. But
              many bars, cafés and restaurants change drinks based on season,
              purchase, availability, time of day or concept.
            </p>

            <p>
              A digital drinks menu is especially useful when guests need to
              understand current options quickly: cocktails, wines, craft
              drinks, non-alcoholic alternatives, coffee specialties, aperitifs,
              digestifs or house recommendations.
            </p>

            <h3>Technology should improve service and operations</h3>

            <p>
              A digital drinks menu is useful when it helps the bar present
              current options more clearly, reduce outdated information and
              support staff during service.
            </p>

            <p>
              It should not replace good service. For wine, cocktails or
              recommendations, it can prepare and support the conversation
              between guest and staff.
            </p>
          </div>
        </section>

        <section id="use-cases" className="acquaintance">
          <h2>Which drinks work especially well in a digital menu?</h2>

          <p>
            Not every bar needs a complex digital structure. But most bars can
            benefit when frequently changing or explanation-heavy drinks are
            presented more clearly.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Cocktail menu.</li>
            <li className="text__seo-li">Wine list.</li>
            <li className="text__seo-li">Non-alcoholic drinks.</li>
            <li className="text__seo-li">Seasonal drinks.</li>
            <li className="text__seo-li">Happy hour offers.</li>
            <li className="text__seo-li">Coffee and specialties.</li>
            <li className="text__seo-li">House recommendations.</li>
            <li className="text__seo-li">Unavailable drinks.</li>
          </ul>

          <p>
            A digital drinks menu is strongest when it gives short orientation:
            taste, style, intensity, non-alcoholic option or seasonal context.
          </p>

          <VisualIdea>
            Smartphone with a digital drinks menu: cocktails, wines,
            non-alcoholic drinks and seasonal recommendations as clear
            categories.
          </VisualIdea>
        </section>

        <section id="structure" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>How should a digital drinks menu be structured?</h2>

            <p>
              A good drinks menu needs order. Guests should not have to scroll
              through a long mixed list when they are looking for a cocktail, a
              glass of wine or a non-alcoholic recommendation.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Separate categories clearly:</strong> For example
                cocktails, wine, beer, non-alcoholic, coffee and
                recommendations.
              </li>

              <li className="text__seo-li">
                <strong>Use short descriptions:</strong> Explain taste, style or
                special ingredients briefly.
              </li>

              <li className="text__seo-li">
                <strong>Keep prices visible:</strong> Guests should not need to
                search for them.
              </li>

              <li className="text__seo-li">
                <strong>Manage availability:</strong> Hide unavailable drinks or
                mark them clearly.
              </li>

              <li className="text__seo-li">
                <strong>Highlight recommendations:</strong> Not every drink
                needs the same visual weight.
              </li>
            </ol>

            <p>
              In a digital menu, this structure can be adjusted later without
              new printing costs.
            </p>
          </div>
        </section>

        <section id="updates" className="acquaintance">
          <h2>What can a bar update faster online?</h2>

          <p>
            The biggest advantage is not only presentation, but maintenance. A
            bar can update content without printing a new drinks menu each time.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Change prices.</li>
            <li className="text__seo-li">Add new cocktails.</li>
            <li className="text__seo-li">Hide unavailable wines.</li>
            <li className="text__seo-li">
              Highlight non-alcoholic drinks.
            </li>
            <li className="text__seo-li">Create seasonal offers.</li>
            <li className="text__seo-li">Add happy hour times.</li>
            <li className="text__seo-li">Improve descriptions.</li>
            <li className="text__seo-li">Replace photos.</li>
          </ul>

          <h3>QR codes can supplement beverage advice</h3>

          <p>
            For bars, the digital menu does not have to replace every personal
            recommendation. It can add useful details without making the printed
            menu too crowded.
          </p>

          <p>
            This is especially helpful for explanation-heavy drinks such as
            wines, cocktails, seasonal beverages or non-alcoholic alternatives.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What mistakes happen with digital drinks menus?</h2>

            <p>
              A digital drinks menu can be useful, but it should not become
              messy, outdated or too promotional.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Too many drinks without clear categories.
                  </li>
                  <li className="text__seo-li">
                    Unavailable wines or drinks remain visible.
                  </li>
                  <li className="text__seo-li">
                    Happy hour offers are unclear in time.
                  </li>
                  <li className="text__seo-li">
                    Descriptions are too long or too poetic.
                  </li>
                  <li className="text__seo-li">
                    Non-alcoholic alternatives are hidden.
                  </li>
                  <li className="text__seo-li">
                    Prices are not kept current.
                  </li>
                  <li className="text__seo-li">
                    QR code fully replaces staff advice.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Use clear categories.</li>
                  <li className="text__seo-li">
                    Hide unavailable drinks immediately.
                  </li>
                  <li className="text__seo-li">
                    Label time-limited offers clearly.
                  </li>
                  <li className="text__seo-li">
                    Write short useful descriptions.
                  </li>
                  <li className="text__seo-li">
                    Make non-alcoholic options visible.
                  </li>
                  <li className="text__seo-li">
                    Review prices regularly.
                  </li>
                  <li className="text__seo-li">
                    Use the digital menu as a service supplement.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>How would I start as a bar owner?</h2>

          <p>
            I would not digitize the entire drinks menu at once. I would start
            with the section that changes most often or needs the most
            explanation.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Choose one main category:</strong> For example cocktails,
              wines or non-alcoholic drinks.
            </li>

            <li className="text__seo-li">
              <strong>Add 10 to 20 drinks:</strong> With price and a short
              description.
            </li>

            <li className="text__seo-li">
              <strong>Mark recommendations:</strong> Not every drink should feel
              equally important.
            </li>

            <li className="text__seo-li">
              <strong>Check on smartphone:</strong> The menu must be fast and
              easy to read.
            </li>

            <li className="text__seo-li">
              <strong>Test availability:</strong> Hide one drink and activate it
              again later.
            </li>

            <li className="text__seo-li">
              <strong>Expand afterwards:</strong> Add more categories only after
              the first real test.
            </li>
          </ol>

          <p>
            This keeps the drinks menu manageable instead of turning it into a
            long, hard-to-read list.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should you do now?</h2>

            <p>
              If you want to create a digital drinks menu for your bar, start
              with a small test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Create a digital menu.</li>
              <li className="text__seo-li">Add one drinks category.</li>
              <li className="text__seo-li">Enter 10 to 20 real drinks.</li>
              <li className="text__seo-li">
                Add short descriptions and prices.
              </li>
              <li className="text__seo-li">
                Open the menu through the QR code on a smartphone.
              </li>
              <li className="text__seo-li">
                Hide one unavailable drink as a test.
              </li>
              <li className="text__seo-li">
                Then expand cocktails, wine or non-alcoholic options.
              </li>
            </ul>

            <VisualIdea>
              A small bar menu test: one drinks category, 10 real drinks,
              visible prices, short descriptions and one unavailable drink
              hidden from the public menu.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ about digital drinks menus</h2>

          <div className="seoFaq">
            <details>
              <summary>What is a digital drinks menu?</summary>
              <p>
                It is an online-managed menu for cocktails, wine, non-alcoholic
                drinks, coffee, beer or seasonal offers that guests can open by
                QR code.
              </p>
            </details>

            <details>
              <summary>
                Is a digital drinks menu only useful for bars?
              </summary>
              <p>
                No. It can also help restaurants, cafés, hotels, wine bars,
                cocktail bars or event locations.
              </p>
            </details>

            <details>
              <summary>Do I need to remove the printed drinks menu?</summary>
              <p>
                Not necessarily. A combination often works best: printed menu
                for a quick overview and QR code for current or detailed
                information.
              </p>
            </details>

            <details>
              <summary>Can I hide unavailable drinks?</summary>
              <p>
                Yes. Unavailable drinks can be temporarily hidden and activated
                again later.
              </p>
            </details>

            <details>
              <summary>Can I highlight non-alcoholic options?</summary>
              <p>
                Yes. A digital menu is useful for making non-alcoholic drinks,
                seasonal beverages or recommendations more visible.
              </p>
            </details>

            <details>
              <summary>Does the QR code stay the same?</summary>
              <p>
                Yes, if the QR code points to a stable menu link. You can update
                content without reprinting the QR code.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="menu"
          exclude="/en/digital-drinks-menu-bar"
        />
      </article>
    </main>
  );
}